import dayjs, { Dayjs } from "dayjs";
import { ReactNode } from "react";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import FAIcon from "../../../common/FAIcon/FAIcon";

export interface Color {
    label: MessageDescriptor;
    color: string;
    key: string;
}

export interface ISunkhronosTexts {
    project: string;
    staff_type: string;
}

export interface ISunkhronosUserCode {
    display: "hidden" | "read-only" | "read-write";
    position: -1 | 0 | 1;
}

export interface CustomFile extends File {
    blob?: string;  // Ajoute une propriété blob de type string (Base64)
}

export interface General {
    name: string;
    type: number;
    language: string;
    image?: CustomFile;
    billing: boolean;
}

export interface NetworkGeneral {
    name: string;
    type: number;
    language: string;
    image?: CustomFile;
    entry_date?: string;
    billing: boolean;
}

export interface ISunkhronosMobileMenu {
    key: string;
    name: MessageDescriptor;
    enabled?: boolean;
}

export interface ISunkhronosMobileDisplay {
    key: string;
    name: MessageDescriptor;
    enabled?: boolean;
}

export interface ISunkhronosMobile {
    image?: CustomFile;
    mobileMenu: ISunkhronosMobileMenu[];
    colorMenu: Color[];
    mobileDisplay: ISunkhronosMobileDisplay[];
    logoRectangle: boolean;
    logoRounded: boolean;
    loginValidityDuration: number;
}

export type ISunkhronosModuleSetting = {
    label: MessageDescriptor;
    key: string;
    settings?: ISunkhronosModuleSetting[];
} & (
        | {
            type: "bool";
            value?: boolean;
            options?: undefined;
        }
        | {
            type: "number";
            value?: number;
            options?: undefined;
        }
        | {
            type: "string";
            value?: string;
            options?: undefined;
        }
        | {
            type: "select";
            value?: any;
            options: { value: any; label: MessageDescriptor; disabled?: boolean; }[];
        }
        | {
            type: "date";
            value?: Dayjs;
            options?: undefined;
            placeholder?: MessageDescriptor;
            information?: ReactNode;
            prefix?: ReactNode;
        }
    );


export interface ISunkhronosMessage {
    title: {
        fr: string;
        de: string;
        en: string;
    };
    content: {
        fr: string;
        de: string;
        en: string;
    },
    key: string;
    enabled?: boolean;
    enabledImage?: boolean;
    label: MessageDescriptor;
    images?: CustomFile[];
    realTitle?: string;
    realContent?: string;
}

export interface ISunkhronosModule {
    nameSection: MessageDescriptor;
    key: string;
    settings: ISunkhronosModuleSetting[];
    enabled?: boolean;
    showSwitch?: boolean;
    companyTypes?: number[];
    icon?: string; // make sure to import the icone in webevolutionPack too
}

export const SunkhronosCompanyTypes = {
    NORMAL_OLD: 1,
    CCNT_HOTELS: 2,
    NORMAL: 3,
    CCT_SECURITY: 4,
} as const;

export const SunkhronosModules: ISunkhronosModule[] = [
    {
        nameSection: defineMessage({ defaultMessage: "CCNT" }),
        key: "ccnt",
        enabled: true,
        showSwitch: false,
        icon: "chart-simple",
        companyTypes: [SunkhronosCompanyTypes.CCNT_HOTELS],
        settings: [
            {
                label: defineMessage({ defaultMessage: "Entry date" }),
                key: "entry_date",
                type: "date",
                value: dayjs(),
                placeholder: defineMessage({ defaultMessage: "Entry date" }),
                prefix: <FAIcon prefix='fad' name='door-open' />,
                information: (
                    <>
                        <FormattedMessage defaultMessage="Editing this field will impact the CCNT previsional calculations." />
                        <br />
                        <FormattedMessage defaultMessage="It is also required for the CCNT companies uses Hotela." />
                    </>
                )
            },
            {
                label: defineMessage({ defaultMessage: "Night work duration threshold" }),
                key: "night_work_duration_threshold",
                type: "number",
                value: 0
            },
        ]
    },
    {
        nameSection: defineMessage({ defaultMessage: "Time clock" }),
        key: "timeclock",
        enabled: true,
        icon: "stopwatch",
        companyTypes: [SunkhronosCompanyTypes.CCNT_HOTELS, SunkhronosCompanyTypes.NORMAL, SunkhronosCompanyTypes.CCT_SECURITY],
        settings: [
            {
                label: defineMessage({ defaultMessage: "Number physical" }),
                key: "physical",
                type: "number"
            },
            {
                label: defineMessage({ defaultMessage: "Confirm authorization" }),
                key: "confirm_authorization",
                type: "select",
                options: [{ value: 'admin', label: defineMessage({ defaultMessage: "Admin" }) }, { value: 'groupadmin', label: defineMessage({ defaultMessage: 'Groupadmin' }) }, { value: 'groupadmin+', label: defineMessage({ defaultMessage: 'Groupadmin+' }) }],
                value: "groupadmin+"
            },
            {
                label: defineMessage({ defaultMessage: "Read authorization" }),
                key: "read_authorization",
                type: "select",
                options: [{ value: 'admins', label: defineMessage({ defaultMessage: 'Admins' }) }, { value: 'groupadmins', label: defineMessage({ defaultMessage: 'Groupadmins' }) }, { value: 'users', label: defineMessage({ defaultMessage: 'Users' }) }],
                value: "admins"
            },
            {
                label: defineMessage({ defaultMessage: "Without Events" }),
                key: "without_events",
                type: "bool",
                value: true
            },
            {
                label: defineMessage({ defaultMessage: "Breaktimes" }),
                key: "breaktimes",
                type: "bool",
                value: true
            },
            {
                label: defineMessage({ defaultMessage: "Split events" }),
                key: "split_events",
                type: "bool",
                value: true
            },
            {
                label: defineMessage({ defaultMessage: "Mobile" }),
                key: "mobile_enabled",
                type: "bool",
                settings: [
                    {
                        label: defineMessage({ defaultMessage: "Mobile without events" }),
                        key: "mobile_without_events",
                        type: "bool",
                        value: true
                    },
                    {
                        label: defineMessage({ defaultMessage: "Mobile requires poi" }),
                        key: "mobile_requires_poi",
                        type: "bool",
                        value: true
                    },
                    {
                        label: defineMessage({ defaultMessage: "Mobile breaktimes" }),
                        key: "mobile_breaktimes",
                        type: "bool",
                        value: true
                    },
                    {
                        label: defineMessage({ defaultMessage: "Mobile details" }),
                        key: "mobile_details",
                        type: "bool",
                        value: true
                    },
                    {
                        label: defineMessage({ defaultMessage: "Mobile pin" }),
                        key: "mobile_pin",
                        type: "bool",
                        value: true
                    },
                ]

            },
        ],
    },
    {
        nameSection: defineMessage({ defaultMessage: "Productivity" }),
        key: "productivity",
        enabled: false,
        settings: [],
        icon: "clipboard-list",
        companyTypes: [SunkhronosCompanyTypes.CCNT_HOTELS],
    },
    {
        nameSection: defineMessage({ defaultMessage: "Report Quadrigis" }),
        key: "report_quadrigis",
        enabled: false,
        settings: [],
        icon: "file-invoice",
        companyTypes: [SunkhronosCompanyTypes.CCT_SECURITY],
    },
    {
        nameSection: defineMessage({ defaultMessage: "Report Normal" }),
        key: "report_normal",
        enabled: false,
        settings: [],
        icon: "file-invoice",
        companyTypes: [SunkhronosCompanyTypes.NORMAL],
    },
    {
        nameSection: defineMessage({ defaultMessage: "Signature" }),
        key: "signature",
        enabled: false,
        settings: [],
        icon: "signature",
        companyTypes: [SunkhronosCompanyTypes.CCNT_HOTELS],
    }
];

export const SunkhronosMessages: ISunkhronosMessage[] = [
    {
        title: {
            fr: "Bienvenue sur Sunkhronos",
            de: "Willkommen bei Sunkhronos",
            en: "Welcome to Sunkhronos"
        },
        content: {
            fr: "Toute l'équipe vous souhaite la bienvenue sur Sunkhronos, votre nouvel outil de gestion du temps et de communication interne.\n\nLe fil d'actualité : Ici, vous retrouverez toutes les informations importantes de votre direction et pourrez suivre les actualités de votre établissement.\n\nVotre code PIN : Il est disponible sur votre profil. En cas d'oubli pour badger facilement.\n\nDemandes d'indisponibilité : Faites-les directement via l'application dans les onglets latéraux.\n\nVotre planning : Consultez-le en temps réel et soyez informé des mises à jour.\n\nMessagerie & Fil utilisateur : Recevez des notifications importantes et publiez des postes si besoin.\n\nBonne prise en main et bienvenue dans l'univers Sunkhronos ! 🎉",
            de: "Das gesamte Team heißt Sie herzlich willkommen bei Sunkhronos, Ihrem neuen Tool für Zeitmanagement und interne Kommunikation.\n\nDer Nachrichtenfeed: Hier finden Sie alle wichtigen Informationen Ihrer Leitung und können die Neuigkeiten Ihrer Einrichtung verfolgen.\n\nIhr PIN-Code: Er ist in Ihrem Profil verfügbar. Nützlich für einfaches Einchecken, falls Sie ihn vergessen haben.\n\nAbwesenheitsanfragen: Stellen Sie diese direkt über die Anwendung in den seitlichen Tabs.\n\nIhr Zeitplan: Überprüfen Sie ihn in Echtzeit und bleiben Sie über Aktualisierungen informiert.\n\nNachrichten & Benutzerfeed: Erhalten Sie wichtige Benachrichtigungen und veröffentlichen Sie bei Bedarf Beiträge.\n\nViel Erfolg bei der Einarbeitung und herzlich willkommen in der Welt von Sunkhronos! 🎉",
            en: "The entire team welcomes you to Sunkhronos, your new time management and internal communication tool.\n\nNews feed: Here, you will find all important information from your management and can follow the news of your establishment.\n\nYour PIN code: It is available on your profile. Useful for easy badging in case you forget it.\n\nUnavailability requests: Make them directly through the application in the side tabs.\n\nYour schedule: Check it in real time and stay informed of updates.\n\nMessaging & User feed: Receive important notifications and publish posts if needed.\n\nEnjoy getting started and welcome to the Sunkhronos universe! 🎉"
        },
        key: "news_feed",
        label: defineMessage({ defaultMessage: "News feed" }),
        images: [],
        enabledImage: false
    },
    {
        title: {
            fr: "Bienvenue sur votre espace de communication d'équipe !",
            de: "Willkommen in Ihrem Team-Kommunikationsbereich!",
            en: "Welcome to your team communication space!"
        },
        content: {
            fr: "Cet espace est là pour vous, les membres de l'équipe.\n\nVous pouvez y échanger librement, partager des photos, des documents, poser des questions ou simplement passer un message à vos collègues.\nC'est votre fil d'actualité interne, accessible à tous les collaborateurs.\n\nRestez respectueux, bienveillants, et faites vivre cet espace à votre image !\n\nBonne utilisation et à bientôt ici !",
            de: "Dieser Bereich ist für Sie, die Teammitglieder, da.\n\nSie können hier frei kommunizieren, Fotos und Dokumente teilen, Fragen stellen oder einfach eine Nachricht an Ihre Kollegen senden.\nDies ist Ihr interner Newsfeed, der allen Mitarbeitern zugänglich ist.\n\nBleiben Sie respektvoll und wohlwollend, und gestalten Sie diesen Bereich nach Ihrem Bild!\n\nViel Erfolg bei der Nutzung und bis bald hier!",
            en: "This space is here for you, the team members.\n\nYou can freely exchange ideas, share photos, documents, ask questions, or simply send a message to your colleagues.\nThis is your internal news feed, accessible to all employees.\n\nStay respectful, kind, and make this space reflect who you are!\n\nEnjoy using it and see you soon here!"
        },
        key: "user_feed",
        label: defineMessage({ defaultMessage: "User feed" }),
        images: [],
        enabledImage: false
    },
    {
        title: {
            fr: "Espace messages – Informations officielles",
            de: "Nachrichten-Bereich – Offizielle Informationen",
            en: "Messages area – Official information"
        },
        content: {
            fr: "Bienvenue dans votre espace Messages. Vous trouverez ici toutes les communications officielles de l'administration : changements de shift, notifications importantes, documents à consulter ou à valider. Cet espace est à sens unique : vous ne pouvez pas y répondre, mais vous pouvez accuser réception en cliquant sur \"Vu\" lorsque cela est demandé.",
            de: "Willkommen in Ihrem Nachrichten-Bereich. Hier finden Sie alle offiziellen Mitteilungen der Verwaltung: Schichtwechsel, wichtige Benachrichtigungen, Dokumente zur Einsicht oder Validierung. Dieser Bereich ist einseitig: Sie können nicht antworten, aber Sie können den Empfang bestätigen, indem Sie auf \"Gesehen\" klicken, wenn dies erforderlich ist.",
            en: "Welcome to your Messages area. Here you will find all official communications from the administration: shift changes, important notifications, documents to review or validate. This area is one-way: you cannot reply, but you can acknowledge receipt by clicking \"Seen\" when requested."
        },
        key: "message",
        label: defineMessage({ defaultMessage: "Message" })
    }
];

const SunkhronosMobileColors: Color[] = [
    {
        color: "#8a0a10",
        label: defineMessage({ defaultMessage: "Primary" }),
        key: "primary"
    },
    {
        color: "#aa1319",
        label: defineMessage({ defaultMessage: "Secondary" }),
        key: "secondary"
    },
    {
        color: "#af191f",
        label: defineMessage({ defaultMessage: "Button" }),
        key: "button"
    },
];

const SunkhronosMobileMenu: ISunkhronosMobileMenu[] = [
    {
        name: defineMessage({ defaultMessage: "Availabilities" }),
        key: "availabilities"
    },
    {
        name: defineMessage({ defaultMessage: "Global Planning" }),
        key: "global_planning"
    },
    {
        name: defineMessage({ defaultMessage: "Emergency" }),
        key: "emergency",
        enabled: true
    },
    {
        name: defineMessage({ defaultMessage: "Documents" }),
        key: "documents",
        enabled: true
    },
    {
        name: defineMessage({ defaultMessage: "Team" }),
        key: "team",
        enabled: true
    },
];

const SunkhronosMobileDisplay: ISunkhronosMobileDisplay[] = [
    {
        name: defineMessage({ defaultMessage: "Planning" }),
        key: "planning",
        enabled: true
    },
    {
        name: defineMessage({ defaultMessage: "Messages" }),
        key: "messages",
        enabled: true
    },
    {
        name: defineMessage({ defaultMessage: "News feed" }),
        key: "news_feed",
        enabled: true
    },
    {
        name: defineMessage({ defaultMessage: "User feed" }),
        key: "user_feed",
        enabled: true
    },
];

export const SunkhronosMobile: ISunkhronosMobile = {
    mobileMenu: SunkhronosMobileMenu,
    colorMenu: SunkhronosMobileColors,
    mobileDisplay: SunkhronosMobileDisplay,
    logoRectangle: false,
    logoRounded: false,
    loginValidityDuration: 365
}

